import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import CreditCardOffRounded from '@mui/icons-material/CreditCardOffRounded';

import Modal from '../components/Modal';
import { v2Colors } from '../design/theme';

const REDIRECT_MS = 5000;
const TICK_MS = 100;

const bodyFont = {
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
};

/**
 * Plug-and-play gate that shows a "credit card payments are having issues" notice
 * before running the online payment flow, then auto-continues after 5 seconds
 * (countdown + progress bar). No Cancel/Continue buttons. Interim measure while the
 * native SDKs still expose the Card option (removing it needs an app-store release).
 *
 * Usage:
 *   const paymentIssueGate = usePaymentIssueGate();
 *   onClick={() => paymentIssueGate.requestPayment(handlePayOnline)}
 *   {paymentIssueGate.modal}
 *
 * @returns {{ open: boolean, requestPayment: (onContinue: () => void) => void, modal: React.ReactNode }}
 */
export function usePaymentIssueGate() {
  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const continueRef = useRef(null);
  const firedRef = useRef(false);
  const intervalRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fireContinue = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    clearTimer();
    setOpen(false);
    const fn = continueRef.current;
    continueRef.current = null;
    if (typeof fn === 'function') fn();
  }, [clearTimer]);

  const requestPayment = useCallback((onContinue) => {
    continueRef.current = onContinue;
    firedRef.current = false;
    setElapsed(0);
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const e = Date.now() - start;
      if (e >= REDIRECT_MS) {
        setElapsed(REDIRECT_MS);
        fireContinue();
      } else {
        setElapsed(e);
      }
    }, TICK_MS);
    return clearTimer;
  }, [open, fireContinue, clearTimer]);

  const progress = Math.min(100, (elapsed / REDIRECT_MS) * 100);
  const secondsLeft = Math.max(1, Math.ceil((REDIRECT_MS - elapsed) / 1000));

  const modal = (
    <Modal open={open} title="Payment notice" maxWidth={400}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          gap: 2,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            width: '100%',
            boxSizing: 'border-box',
            bgcolor: v2Colors.selectedFill,
            borderRadius: 2,
            border: `1px solid ${v2Colors.border}`,
            px: 1.75,
            py: 1.75,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: v2Colors.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CreditCardOffRounded sx={{ color: v2Colors.primary, fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
            <Typography
              sx={{
                ...bodyFont,
                fontSize: '0.9375rem',
                fontWeight: 700,
                color: v2Colors.primary,
                lineHeight: 1.4,
                textAlign: 'left',
              }}
            >
              We are currently experiencing issues with credit card payments.
            </Typography>
            <Typography
              sx={{
                ...bodyFont,
                fontSize: '0.8125rem',
                color: v2Colors.textMuted,
                mt: 0.75,
                lineHeight: 1.5,
                textAlign: 'left',
              }}
            >
              Please use UPI, Net Banking, or Wallets to complete your payment.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                ...bodyFont,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: v2Colors.heading,
                textAlign: 'left',
              }}
            >
              Initiating payment…
            </Typography>
            <Typography
              sx={{
                ...bodyFont,
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: v2Colors.primary,
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'right',
                flexShrink: 0,
              }}
            >
              {secondsLeft}s
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              width: '100%',
              height: 6,
              borderRadius: 3,
              bgcolor: v2Colors.selectedFill,
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: v2Colors.primary,
                transition: 'transform 0.1s linear',
              },
            }}
          />
        </Box>
      </Box>
    </Modal>
  );

  return { open, requestPayment, modal };
}
